// When does a drive end by itself? Not at a fence (you also park at the lumber yard, at home, at a job you
// are not clocked into) — when the truck has been parked a while. The clock keeps a stillness anchor for the
// running drive (meta/clock.travelAnchor {lat,lng,t} + travelMovedAt): a fix that lands farther than
// TRAVEL_STILL_M from the anchor moves it; TRAVEL_STILL_MS of fixes inside it = parked, the drive ended when
// it last moved. Pure — the foreground watcher, the headless recorder and the app's idle check all feed it.
import { distM } from './segment.js';

export const TRAVEL_STILL_MS = 10 * 60000;   // parked this long = the drive is over
export const TRAVEL_STILL_M = 75;            // moved less than this = still parked (GPS jitter + a big lot)
export const TRAVEL_NO_FIX_MS = 20 * 60000;  // a drive without a single fix in this long is one the phone never saw
export const DRIVE_START_KMH = 25;           // slower than this is walking, or a machine on site
export const DRIVE_START_M = 300;            // and this far from where the stretch began
export const DRIVE_START_WINDOW_MS = 3 * 60000;
export const FENCE_MIN_APART_M = 300;        // two jobs this close share a fence: one of them is home or the shop

/**
 * Feed fixes ({lat,lng,t}) to the running drive's stillness watch.
 * @returns {{endAt:number}} parked ≥ stillMs → the drive ended at the last move; {{patch}} the anchor moved
 *   (write it onto meta/clock so the next wake carries on); null = nothing to do.
 */
export function travelStillness(clock, points, { stillMs = TRAVEL_STILL_MS, radiusM = TRAVEL_STILL_M } = {}) {
  if (!clock?.traveling) return null;
  let anchor = clock.travelAnchor || null;
  let movedAt = clock.travelMovedAt || null;
  const since = clock.travelStartedAt || 0;
  const pts = (points || [])
    .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && Number.isFinite(p.t) && p.t >= since)
    .sort((a, b) => a.t - b.t);
  let moved = false;
  for (const p of pts) {
    if (!anchor || distM(anchor, p) > radiusM) { anchor = { lat: p.lat, lng: p.lng, t: p.t }; movedAt = p.t; moved = true; continue; }
    if (movedAt && p.t - movedAt >= stillMs) return { endAt: movedAt };
  }
  return moved ? { patch: { travelAnchor: anchor, travelMovedAt: movedAt } } : null;
}

/**
 * No fixes arriving (iOS pauses updates once parked, GPS off, phone in a drawer): judge the drive by its clock.
 * @returns {{endAt:number, noFix?:true}} ended at its last move; noFix = never got a fix — nothing was recorded.
 */
export function travelIdleEnd(clock, now = Date.now(), { stillMs = TRAVEL_STILL_MS, noFixMs = TRAVEL_NO_FIX_MS } = {}) {
  if (!clock?.traveling) return null;
  if (clock.travelMovedAt) return now - clock.travelMovedAt >= stillMs ? { endAt: clock.travelMovedAt } : null;
  if (clock.travelStartedAt && now - clock.travelStartedAt >= noFixMs) return { endAt: clock.travelStartedAt, noFix: true };
  return null;
}

/**
 * Is the truck moving? Clocked in and driving is a drive, wherever the clock-in happened: a job's fence only arms when
 * you've been seen inside it, so clocking in at home and leaving for the lumber yard recorded nothing (Oct 29, Oct 31).
 * Two ways to say yes: the phone's own speed on the newest two fixes, or a stretch of `minM`+ over the last `windowMs`
 * at driving speed — walking a big site never reaches it. → { at } (when that stretch began) or null.
 */
export function drivingNow(points, { minKmh = DRIVE_START_KMH, minM = DRIVE_START_M, windowMs = DRIVE_START_WINDOW_MS } = {}) {
  const pts = (points || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && Number.isFinite(p.t) && trustedFix(p)).sort((a, b) => a.t - b.t);
  if (pts.length < 2) return null;
  const last = pts[pts.length - 1]; const prev = pts[pts.length - 2];
  const fast = (p) => typeof p.speed === 'number' && p.speed * 3.6 >= minKmh;
  if (fast(last) && fast(prev) && last.t - prev.t <= windowMs) return { at: prev.t };
  const near = pts.filter((p) => last.t - p.t <= windowMs && p !== last);
  const from = near.find((p) => distM(p, last) >= minM && (distM(p, last) / 1000) / ((last.t - p.t) / 3600000) >= minKmh);
  return from ? { at: from.t } : null;
}

/**
 * Where a job's fence may go when it has none. A fence set where you stood at the first clock-in lands at home or the
 * shop when that clock-in was estimating: Ken's and Mori's are the same spot, 15 km from Riondel. So: not within
 * `minM` of another job's fence. → true when this spot can be the job's fence.
 */
export function fenceSpotFree(loc, projects = [], projectId = null, minM = FENCE_MIN_APART_M) {
  if (!loc || !Number.isFinite(loc.lat) || !Number.isFinite(loc.lng)) return false;
  return !(projects || []).some((p) => p && p.id !== projectId && p.geofence && Number.isFinite(p.geofence.lat) && distM(p.geofence, loc) < minM);
}

/** A fix the stillness watch may trust: accuracy unknown or tighter than `maxAccM` (a cell fix can be 500 m off). */
export const trustedFix = (p, maxAccM = 150) => !!p && !(typeof p.acc === 'number' && p.acc > maxAccM);
